import { createFileRoute } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { PHYSICIAN_ID, PHYSICIAN_SAME_AS } from "@/lib/medical-schema";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { LazyMapFrame } from "@/components/site/LazyMapFrame";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { sendContactInquiry } from "@/lib/inquiries.functions";
import { measureConversion } from "@/lib/cta-measurement";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const TOPIC_PREFILL: Record<string, string> = {
  support:
    "I'd like to join the bariatric support group. Please send me the meeting details.",
  insurance:
    "Please verify whether I have bariatric coverage.",
  "self-pay-callback":
    "I would like Val or Jamie to call me to discuss the self-pay option.",
};

export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): { topic?: string } =>
    typeof search["topic"] === "string" ? { topic: search["topic"] } : {},
  head: ({ match }) => {
    // Filtered variants like /contact?topic=support are the same page with a
    // prefilled message. They canonicalize to the clean URL, so they must NOT
    // carry the hreflang cluster (an hreflang set on a non-canonical URL has no
    // self-reference and is reported as an hreflang conflict).
    const isFiltered = Boolean((match.search as { topic?: string } | undefined)?.topic);

    return {
    meta: [
      { title: "Contact Virginia Bariatric Surgery | Herndon, VA" },
      {
        name: "description",
        content:
          "Get in touch with Virginia Bariatric Surgery in Herndon, VA. Call (703) 709-9771 or send Dr. Fitzer a message.",
      },
      ...(isFiltered ? [{ name: "robots", content: "noindex,follow" }] : []),
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Contact Virginia Bariatric Surgery | Herndon, VA" },
        { property: "og:description", content: "Get in touch with Virginia Bariatric Surgery in Herndon, VA. Call (703) 709-9771 or send Dr. Fitzer a message." },
        { property: "og:url", content: "https://virginiabariatric.com/contact" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
        { name: "twitter:title", content: "Contact Virginia Bariatric Surgery | Herndon, VA" },
        { name: "twitter:description", content: "Get in touch with Virginia Bariatric Surgery in Herndon, VA. Call (703) 709-9771 or send Dr. Fitzer a message." },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/contact") : []),
      ...(isFiltered
        ? []
        : [
            
            
          ]),
    ],

    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              // ContactPage node so the Spanish twin has an English counterpart
              // to point at with translationOfWork.
              "@type": "ContactPage",
              "@id": "https://virginiabariatric.com/contact#webpage",
              url: "https://virginiabariatric.com/contact",
              name: "Contact Virginia Bariatric Surgery",
              inLanguage: "en-US",
              isPartOf: { "@id": "https://virginiabariatric.com/#website" },
              publisher: { "@id": "https://virginiabariatric.com/#organization" },
              ...workTranslationOf("/contact"),
            },
            // The MedicalClinic/Organization and physician Person nodes are
            // emitted once sitewide from src/routes/__root.tsx, so this page
            // adds only the contact-specific point of contact.
            {
              "@type": "ContactPoint",
              "@id": "https://virginiabariatric.com/contact#appointments",
              contactType: "Appointments",
              telephone: "+1-703-709-9771",
              email: "Val@virginiabariatric.com",
              areaServed: "US",
              availableLanguage: ["English", "Spanish"],
              hoursAvailable: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: [
                    "https://schema.org/Monday",
                    "https://schema.org/Tuesday",
                    "https://schema.org/Wednesday",
                    "https://schema.org/Thursday",
                    "https://schema.org/Friday",
                  ],
                  opens: "08:00",
                  closes: "16:30",
                },
              ],
            },


          ],
        }),
      },
    ] : [],
    };
  },

  component: ContactPage,
});


function ContactPage() {
  const { topic } = Route.useSearch();
  const prefilledMessage = topic ? (TOPIC_PREFILL[topic] ?? "") : "";
  const [sent, setSent] = useState(false);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitContact = useServerFn(sendContactInquiry);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }

    setSending(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      phone: String(formData.get("phone") || "").trim() || undefined,
      email: String(formData.get("email") || "").trim(),
      message: String(formData.get("message") || "").trim(),
      turnstileToken,
    };

    try {
      await submitContact({ data: payload });
      measureConversion("contact_request");
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageSection className="pt-4 pb-3 text-center">
        <EditableText as="h1" contentKey="contact.hero.title" defaultValue="Contact Us" className="text-4xl md:text-5xl font-serif" />
        <EditableText as="p" contentKey="contact.hero.body" multiline defaultValue="Call, email, or use the form to request an appointment or ask a question. We will get back to you pronto." className="mt-2 max-w-2xl mx-auto text-muted-foreground" />
      </PageSection>

      <PageSection className="pb-6 grid gap-2 md:grid-cols-2">
          <div className="flex h-full flex-col gap-2">
            <aside className="flex">
              <div className="flex-1 rounded-2xl bg-brand-soft/50 p-5 md:p-6">
                <EditableText as="h2" contentKey="contact.office.title" defaultValue="Office Address" className="font-serif text-2xl" />
                <address className="not-italic mt-2 text-foreground/80 leading-[1.3] text-[0.9rem]">
                  <EditableText as="span" contentKey="contact.office.line1" defaultValue="Virginia Bariatric Surgery" /><br />
                  <EditableText as="span" contentKey="contact.office.line2" defaultValue="171 Elden Street, Suite 3B" /><br />
                  <EditableText as="span" contentKey="contact.office.line3" defaultValue="Herndon, VA 20170" />
                </address>
                <p className="mt-3 text-sm text-muted-foreground">Office hours: Monday–Friday, 8:00 am – 4:30 pm</p>
              </div>
            </aside>

            <aside className="flex">
              <dl className="flex-1 divide-y divide-border rounded-2xl border border-border bg-card">
                <div className="p-4 flex items-baseline justify-between gap-4">
                  <dt className="text-sm uppercase tracking-wider text-muted-foreground">Phone</dt>
                  <dd className="text-[0.9rem]"><a className="text-brand-logo font-medium" aria-label="Call Virginia Bariatric Surgery at 703-709-9771" href="tel:7037099771"><EditableText as="span" contentKey="contact.phone" defaultValue="(703) 709-9771" /></a></dd>
                </div>
                <div className="p-4 flex items-baseline justify-between gap-4">
                  <dt className="text-sm uppercase tracking-wider text-muted-foreground">Fax</dt>
                  <dd className="text-foreground/80 text-[0.9rem]"><EditableText as="span" contentKey="contact.fax" defaultValue="(888) 519-0045" /></dd>
                </div>
                <div className="p-4 flex items-baseline justify-between gap-4">
                  <dt className="text-sm uppercase tracking-wider text-muted-foreground">Email</dt>
                  <dd className="text-[0.9rem]"><a className="text-brand-logo font-medium break-words" href="mailto:Val@virginiabariatric.com"><EditableText as="span" contentKey="contact.email" defaultValue="Val@virginiabariatric.com" /></a></dd>
                </div>
              </dl>
            </aside>
          </div>

          <form
            ref={formRef}
            className="h-full rounded-2xl border border-border bg-card p-4 md:p-5 shadow-sm space-y-1.5"
            onSubmit={handleSubmit}
          >
            {sent ? (
              <div className="py-8 text-center">
                <EditableText as="h2" contentKey="contact.thanks.title" defaultValue="Thank you" className="text-2xl font-serif" />
                <EditableText as="p" contentKey="contact.thanks.body" multiline defaultValue="Your message has been received. Dr. Fitzer's office will be in touch shortly." className="mt-2 text-muted-foreground" />
              </div>
            ) : (
              <>
                <EditableText as="h2" contentKey="contact.form.title" defaultValue="Send Us a Message" className="font-serif text-2xl" />
                <div className="grid gap-1.5 sm:grid-cols-2">
                  <label htmlFor="contact-name" className="text-sm">
                    <span className="block mb-0.5 font-medium">Name<span aria-hidden="true" className="text-brand">*</span></span>
                    <input id="contact-name" required type="text" name="name" autoComplete="name" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
                  </label>
                  <label htmlFor="contact-phone" className="text-sm">
                    <span className="block mb-0.5 font-medium">Phone</span>
                    <input id="contact-phone" type="tel" name="phone" autoComplete="tel" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
                  </label>
                </div>
                <label htmlFor="contact-email" className="block text-sm">
                  <span className="block mb-0.5 font-medium">Email<span aria-hidden="true" className="text-brand">*</span></span>
                  <input id="contact-email" required type="email" name="email" autoComplete="email" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
                </label>
                <label htmlFor="contact-message" className="block text-sm">
                  <span className="block mb-0.5 font-medium">Message<span aria-hidden="true" className="text-brand">*</span></span>
                  <textarea id="contact-message" required name="message" rows={2} defaultValue={prefilledMessage} className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand resize-y" />
                </label>
                {error && (
                  <p className="text-sm text-red-600" role="alert">
                    {error}
                  </p>
                )}
                <div className="flex justify-center">
                  <LazyTurnstile
                    siteKey={siteKey}
                    triggerRef={formRef}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setError("Security check failed. Please try again.")}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </div>
                <div className="flex justify-center">
                  <button type="submit" disabled={sending || !turnstileToken} className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                    {sending ? "Sending..." : "Send message"}
                  </button>
                </div>
              </>
            )}
          </form>
      </PageSection>

      <PageSection className="pb-8">
        <h2 className="font-serif text-2xl text-center text-brand-deep">Find Our Herndon Office</h2>
        <p className="mt-2 text-center text-muted-foreground">
          Virginia Bariatric Surgery · 171 Elden Street, Suite 3B, Herndon, VA 20170 ·{" "}
          <a className="text-brand-logo font-medium" aria-label="Call Virginia Bariatric Surgery at 703-709-9771" href="tel:7037099771">(703) 709-9771</a>
        </p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border">
          <LazyMapFrame
            title="Google Map showing Virginia Bariatric Surgery at 171 Elden Street, Suite 3B, Herndon, VA 20170"
            src="https://www.google.com/maps?q=171%20Elden%20Street%20Suite%203B%2C%20Herndon%2C%20VA%2020170&output=embed"
          />
        </div>
        <p className="mt-3 text-center text-sm">
          <a
            className="text-brand-logo font-medium hover:underline"
            href="https://www.google.com/maps/search/?api=1&query=171+Elden+Street+Suite+3B+Herndon+VA+20170"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get directions to our Herndon office
          </a>
        </p>
      </PageSection>

    </>
  );
}
